/**
 * Prop Studio — where a prop can go (shelf, basket, the hooks, floor,
 * ceiling, best first), which spaces it is staged in, and the prop's box and
 * hold point: its size, what it keeps clear in front, how high a wall prop is
 * held, and where the gold ball is (along the wall, how far the box hangs
 * below it, out from the wall). Every change is saved at once to the
 * library's catalogue (staging/catalogue.ts), and staging uses it from the
 * next Stage.
 */

import React, { useEffect, useState } from 'react';
import { designerControlStyle as control } from '../designer/DesignerHost';
import { PLACEMENTS, Placement, PropDef, PropSize, STAGING_KITS } from '../../calculations/staging/kits';
import { CatalogueEntry, kitWith, placesOf, propById, spacesOf, togglePlace } from '../../calculations/staging/catalogue';
import type { Catalogue } from '../../calculations/staging/catalogue';
import { NumberField } from './NumberField';
import { propFrame } from './propFrame';
import { PROP_STUDIO_STRINGS as S } from './strings';

const MUTED = '#9a9ca6';
const ACCENT = '#FEB100';
const LABEL: React.CSSProperties = { fontSize: 11, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4 };
const NUMBER: React.CSSProperties = { ...control, width: 58, padding: '3px 6px', textAlign: 'right' };
const ROW: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 4 };
const SPACE_KEYS = Object.keys(STAGING_KITS);
const tidy = (n: number) => Math.round(n * 1000) / 1000;
/**
 * "Placed": the kit's `order` in four steps. First (5) goes before every
 * main piece (the vanity is 10); a main piece (20) is placed once at any
 * density (stage.ts MAIN_ORDER is 30); the rest (50, the default) and last
 * (80, a prop made in Prop Studio) are thinned or multiplied by it.
 */
type PlacedBucket = 'first' | 'main' | 'rest' | 'last';
const PLACED_ORDER: Record<PlacedBucket, number> = { first: 5, main: 20, rest: 50, last: 80 };
const placedBucket = (order: number | undefined): PlacedBucket => {
  const n = order ?? 50;
  return n < 10 ? 'first' : n < 30 ? 'main' : n < 70 ? 'rest' : 'last';
};

export interface PlacesPanelProps {
  def: PropDef;
  catalogue: Catalogue | undefined;
  /** The placement shown in the viewer (one of the prop's places). */
  previewPlace: Placement | undefined;
  onPreviewPlace: (p: Placement) => void;
  onSave: (entry: CatalogueEntry) => Promise<void>;
  onDelete: () => void;
  busy: boolean;
  /** The model's size as it is placed here (feet), shown by "Match the model". None: no model yet. */
  modelSize?: PropSize | null;
  /** "Match the model": the box sized to the model, a held model moved onto the hold point first (PropStudio.tsx matchModel). */
  onMatchModel?: () => void;
}

export const PlacesPanel: React.FC<PlacesPanelProps> = ({ def, catalogue, previewPlace, onPreviewPlace, onSave, onDelete, busy, modelSize, onMatchModel }) => {
  const entry = catalogue?.[def.id];
  const added = !!entry?.added;
  const places = placesOf(def);
  const spaces = spacesOf(def.id, catalogue);
  const [label, setLabel] = useState(def.label);
  useEffect(() => { setLabel(def.label); }, [def.id, def.label]);
  /** The entry as it is, with a change: every field kept, so a save never loses another. */
  const save = (patch: Partial<CatalogueEntry>) => onSave({ ...(entry ?? {}), ...patch });
  const wall = def.kind === 'wall';
  // A built-in wall prop (the towel bar) is fixed to the wall for good; any other prop can be put on the wall and taken off again.
  const builtInWall = wall && propById(def.id)?.kind === 'wall';
  const held = propFrame(def).kind === 'held' && def.kind !== 'ceiling';
  const floorable = def.kind === 'floor' || places.includes('floor');
  // The floor props it could stand in front of: those of the kits of its spaces.
  const facing = new Map<string, string>();
  for (const space of spaces) {
    for (const p of kitWith(space, catalogue).props) if (p.kind === 'floor' && p.id !== def.id && !facing.has(p.id)) facing.set(p.id, p.label);
  }
  const select = (label: string, value: string, options: [string, string][], onChange: (v: string) => void, title?: string) => (
    <select value={value} disabled={busy} onChange={(e) => onChange(e.target.value)} style={{ ...control, padding: '3px 6px' }} aria-label={label} title={title}>
      {options.map(([v, text]) => <option key={v} value={v} style={{ color: '#000' }}>{text}</option>)}
    </select>
  );

  const box = (on: boolean, text: string, onChange: (on: boolean) => void, title?: string, key?: string) => (
    <label key={key ?? text} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: busy ? 'default' : 'pointer' }} title={title}>
      <input type="checkbox" checked={on} disabled={busy} onChange={(e) => onChange(e.target.checked)} style={{ accentColor: ACCENT }} />
      {text}
    </label>
  );
  const field = (label: string, value: number, onCommit: (v: number) => void, opts: { min?: number; max?: number; step?: number } = {}) => (
    <NumberField
      key={label} value={tidy(value)} step={opts.step ?? 0.1} min={opts.min} max={opts.max}
      accept={(v) => (opts.min === undefined || v >= opts.min) && (opts.max === undefined || v <= opts.max)}
      style={NUMBER} aria-label={label} onCommit={(v) => onCommit(tidy(v))}
    />
  );

  // In or out of the pool: the one switch that stops a prop being staged without losing anything about it.
  const poolRow = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', border: `1px solid ${def.disabled ? 'rgba(255,159,159,0.5)' : 'rgba(255,255,255,0.12)'}`, borderRadius: 8 }}>
      {box(!def.disabled, S.catalogue.inPool, (on) => save({ disabled: !on }), S.catalogue.inPoolTitle)}
      {def.disabled && <span style={{ fontSize: 11, color: '#ff9f9f' }}>{S.offTag}</span>}
    </div>
  );
  // The box and the hold point: every prop's, saved to its catalogue entry.
  const boxSection = (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 10px', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8 }} aria-label={S.catalogue.boxTitle}>
      <span style={LABEL} title={S.catalogue.boxHint}>{S.catalogue.boxTitle}</span>
      <div style={ROW} title={S.catalogue.sizeTitle}>
        <span style={{ width: 96, fontSize: 12 }}>{S.catalogue.size}</span>
        {(['w', 'd', 'h'] as const).map((k, i) => (
          <React.Fragment key={k}>
            <span style={{ fontSize: 10, color: MUTED }}>{[S.catalogue.along, S.catalogue.deep, S.catalogue.tall][i]}</span>
            {field(`${S.catalogue.size} ${k}`, def.size[k], (v) => save({ size: { ...def.size, [k]: v } }), { min: 0.05, max: 40 })}
          </React.Fragment>
        ))}
      </div>
      {modelSize && onMatchModel && (
        <div style={{ ...ROW, gap: 8 }} title={held ? S.catalogue.matchModelHeldTitle : S.catalogue.matchModelTitle}>
          <span style={{ width: 96 }} />
          <button
            type="button" disabled={busy}
            style={{ ...control, cursor: busy ? 'default' : 'pointer', fontSize: 12, padding: '3px 9px', whiteSpace: 'nowrap' }}
            onClick={onMatchModel}
          >
            {S.catalogue.matchModel}
          </button>
          <span style={{ fontSize: 10, color: MUTED }}>{S.catalogue.modelIs(tidy(modelSize.w), tidy(modelSize.d), tidy(modelSize.h))}</span>
        </div>
      )}
      <div style={ROW} title={S.catalogue.clearanceTitle}>
        <span style={{ width: 96, fontSize: 12 }}>{S.catalogue.clearance}</span>
        {floorable && (
          <>
            <span style={{ fontSize: 10, color: MUTED }}>{S.catalogue.clearFront}</span>
            {field(`${S.catalogue.clearance} ${S.catalogue.clearFront}`, def.clearance ?? 0, (v) => save({ clearance: v }), { min: 0, max: 40 })}
          </>
        )}
        <span style={{ fontSize: 10, color: MUTED }}>{S.catalogue.clearSides}</span>
        {field(`${S.catalogue.clearance} ${S.catalogue.clearSides}`, def.clearSide ?? 0, (v) => save({ clearSide: v }), { min: 0, max: 40 })}
        {floorable && (
          <>
            <span style={{ fontSize: 10, color: MUTED }}>{S.catalogue.clearBack}</span>
            {field(`${S.catalogue.clearance} ${S.catalogue.clearBack}`, def.clearBack ?? 0, (v) => save({ clearBack: v }), { min: 0, max: 40 })}
          </>
        )}
      </div>
      {wall && (
        <div style={ROW} title={S.catalogue.mountHeightTitle}>
          <span style={{ width: 96, fontSize: 12 }}>{S.catalogue.mountHeight}</span>
          {field(S.catalogue.mountHeight, def.mountHeight ?? 4, (v) => save({ mountHeight: v }), { min: 0, max: 40 })}
        </div>
      )}
      {def.kind === 'yard' && (
        <div style={{ ...ROW, gap: 8, flexWrap: 'wrap' }}>
          <span style={{ width: 96, fontSize: 12 }} title={S.catalogue.standsYardTitle}>{S.catalogue.stands}</span>
          {select(S.catalogue.stands, def.anchor === 'window' ? 'window' : 'lawn', [['lawn', S.catalogue.standsLawn], ['window', S.catalogue.standsWindow]], (v) => save({ anchor: v as CatalogueEntry['anchor'] }), S.catalogue.standsYardTitle)}
        </div>
      )}
      {floorable && !wall && (
        <div style={{ ...ROW, gap: 8, flexWrap: 'wrap' }}>
          <span style={{ width: 96, fontSize: 12 }} title={S.catalogue.standsTitle}>{S.catalogue.stands}</span>
          {select(S.catalogue.stands, def.anchor ?? 'wall', [['wall', S.catalogue.standsWall], ['free', S.catalogue.standsFree], ['any', S.catalogue.standsAny], ['corner', S.catalogue.standsCorner], ['bay', S.catalogue.standsBay]], (v) => save({ anchor: v as CatalogueEntry['anchor'] }), S.catalogue.standsTitle)}
          {def.anchor === 'bay' ? (
            <>
              <span style={{ fontSize: 12 }} title={S.catalogue.headingTitle}>{S.catalogue.heading}</span>
              {select(S.catalogue.heading, def.heading ?? 'any', [['any', S.catalogue.headingAny], ['in', S.catalogue.headingIn], ['out', S.catalogue.headingOut]], (v) => save({ heading: v as CatalogueEntry['heading'] }), S.catalogue.headingTitle)}
              <span style={{ fontSize: 12 }} title={S.catalogue.insideTitle}>{S.catalogue.inside}</span>
              {select(S.catalogue.inside, String(def.inside ?? 1), [['1', S.catalogue.insideAll], ['0.5', S.catalogue.insideHalf], ['0.25', S.catalogue.insideQuarter], ['0', S.catalogue.insideNone]], (v) => save({ inside: Number(v) }), S.catalogue.insideTitle)}
            </>
          ) : (
            <>
              <span style={{ fontSize: 12 }} title={S.catalogue.facesTitle}>{S.catalogue.faces}</span>
              {select(S.catalogue.faces, def.faces ?? '', [['', S.catalogue.facesRoom], ...[...facing.entries()]], (v) => save({ faces: v }), S.catalogue.facesTitle)}
              <span style={{ fontSize: 12 }} title={S.catalogue.besideTitle}>{S.catalogue.beside}</span>
              {select(S.catalogue.beside, def.besides ?? '', [['', S.catalogue.besideNone], ...[...facing.entries()]], (v) => save({ besides: v }), S.catalogue.besideTitle)}
              {def.besides && (
                <>
                  <span style={{ fontSize: 10, color: MUTED }}>{S.catalogue.besideGap}</span>
                  {field(`${S.catalogue.beside} ${S.catalogue.besideGap}`, def.besidesGap ?? 1 / 12, (v) => save({ besidesGap: v }), { min: 0, max: 10 })}
                </>
              )}
              {(def.anchor === 'free' || def.anchor === 'any') && box(!!def.askew, S.catalogue.askew, (on) => save({ askew: on }), S.catalogue.askewTitle)}
            </>
          )}
        </div>
      )}
      {!builtInWall && (
        <div style={{ ...ROW, gap: 8, flexWrap: 'wrap' }} title={S.catalogue.countTitle}>
          <span style={{ width: 96, fontSize: 12 }}>{S.catalogue.count}</span>
          <NumberField
            value={def.count === 'each' ? 1 : def.count ?? 1} step={1} min={1} max={20} accept={(v) => Number.isInteger(v) && v >= 1 && v <= 20} style={NUMBER} aria-label={S.catalogue.count}
            onCommit={(v) => save({ count: v })}
          />
          {box(!!def.fixed, S.catalogue.fixed, (on) => save({ fixed: on }))}
        </div>
      )}
      <div style={{ ...ROW, gap: 8 }} title={S.catalogue.placedTitle}>
        <span style={{ width: 96, fontSize: 12 }}>{S.catalogue.placed}</span>
        {select(S.catalogue.placed, placedBucket(def.order), [['first', S.catalogue.placedFirst], ['main', S.catalogue.placedMain], ['rest', S.catalogue.placedRest], ['last', S.catalogue.placedLast]], (v) => save({ order: PLACED_ORDER[v as PlacedBucket] }), S.catalogue.placedTitle)}
        <span style={{ fontSize: 10, color: MUTED }}>{S.catalogue.placedOrder(def.order ?? 50)}</span>
      </div>
      {held && (
        <div style={ROW} title={S.catalogue.holdTitle}>
          <span style={{ width: 96, fontSize: 12 }}>{S.catalogue.hold}</span>
          <span style={{ fontSize: 10, color: MUTED }}>{S.catalogue.holdAlong}</span>
          {field(`${S.catalogue.hold} ${S.catalogue.holdAlong}`, def.holdX ?? 0, (v) => save({ holdX: v }), { min: -40, max: 40 })}
          <span style={{ fontSize: 10, color: MUTED }}>{S.catalogue.holdBelow}</span>
          {field(`${S.catalogue.hold} ${S.catalogue.holdBelow}`, def.drop ?? 0, (v) => save({ drop: v }), { min: 0, max: 40 })}
          <span style={{ fontSize: 10, color: MUTED }}>{S.catalogue.holdOut}</span>
          {field(`${S.catalogue.hold} ${S.catalogue.holdOut}`, propFrame(def).holdZ, (v) => save({ holdZ: v }), { min: 0, max: 10 })}
        </div>
      )}
      {busy && <span style={{ fontSize: 11, color: ACCENT }}>{S.catalogue.saving}</span>}
    </section>
  );

  if (builtInWall) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {poolRow}
        {boxSection}
        <p style={{ margin: 0, fontSize: 11, color: MUTED }}>{S.catalogue.wallProp}</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {poolRow}
      {boxSection}
      <section style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 10px', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8 }} aria-label={S.catalogue.title}>
        <span style={LABEL} title={S.catalogue.hint}>{S.catalogue.title}</span>
        {/* The name: a built-in prop's too (the catalogue's label replaces the kit's, catalogue.ts withEntry). */}
        {(
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 52, fontSize: 12 }}>{S.catalogue.name}</span>
            <input
              value={label} disabled={busy} aria-label={S.catalogue.name} maxLength={60} style={{ ...control, flex: 1, padding: '3px 6px' }}
              onChange={(e) => setLabel(e.target.value)}
              onBlur={() => { const t = label.trim(); if (t && t !== def.label) save({ label: t }); else setLabel(def.label); }}
              onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            />
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 10px' }}>
          {PLACEMENTS.map((p) => box(places.includes(p), S.catalogue.placeNames[p], (on) => save({ places: togglePlace(places, p, on) }), undefined, p))}
        </div>
        <span style={{ fontSize: 11, color: MUTED }}>{wall ? S.catalogue.wallPlace : S.catalogue.order(places.map((p) => S.catalogue.placeNames[p]).join(' → '))}</span>
        {places.length > 1 && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }} title={S.catalogue.previewAsTitle}>
            {S.catalogue.previewAs}
            <select value={previewPlace ?? places[0]} onChange={(e) => onPreviewPlace(e.target.value as Placement)} style={{ ...control, padding: '3px 6px' }} aria-label={S.catalogue.previewAs}>
              {places.map((p) => <option key={p} value={p} style={{ color: '#000' }}>{S.catalogue.placeNames[p]}</option>)}
            </select>
          </label>
        )}

        <span style={{ ...LABEL, marginTop: 4 }} title={S.catalogue.spacesHint}>{S.catalogue.spaces}</span>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 10px' }}>
          {SPACE_KEYS.map((k) => box(spaces.includes(k), S.spaceTypeName[k] ?? k, (on) => save({ spaces: on ? [...spaces, k] : spaces.filter((x) => x !== k) }), undefined, k))}
        </div>
        {spaces.length === 0 && <span style={{ fontSize: 11, color: ACCENT }}>{S.catalogue.noSpace}</span>}
        {busy && <span style={{ fontSize: 11, color: ACCENT }}>{S.catalogue.saving}</span>}
        {added && (
          <button type="button" disabled={busy} onClick={onDelete} style={{ ...control, cursor: 'pointer', alignSelf: 'flex-start', color: '#ff9f9f', fontSize: 12, padding: '3px 9px' }}>
            {S.catalogue.remove}
          </button>
        )}
      </section>
    </div>
  );
};

export default PlacesPanel;
